/* Shared mock data for analytics Storybook variants.
   Mirrors the shape of data returned by Convex analytics queries. */

export interface OverviewStats {
  today: { items: number; minutes: number };
  thisWeek: { items: number; minutes: number };
  thisMonth: { items: number; minutes: number };
  allTime: { items: number; minutes: number };
  queueSize: number;
  feedSize: number;
}

export interface DailyActivity {
  date: string; // YYYY-MM-DD
  items: number;
  minutes: number;
}

export interface ContentTypeStat {
  type: string;
  completedCount: number;
  totalCount: number;
  completedMinutes: number;
  totalMinutes: number;
}

export interface SourceStat {
  name: string;
  completedCount: number;
  totalCount: number;
  completionRate: number;
  totalMinutes: number;
}

export interface HourlyActivity {
  hour: number;
  count: number;
  minutes: number;
}

export interface DayOfWeekActivity {
  day: string;
  count: number;
  minutes: number;
}

export interface StreakStat {
  goalName: string;
  currentStreak: number;
  longestStreak: number;
  dailyGoalMinutes: number;
}

export interface WeeklyComparison {
  thisWeek: { items: number; minutes: number };
  lastWeek: { items: number; minutes: number };
  itemsChange: number; // percentage
  minutesChange: number; // percentage
}

export interface TagTime {
  tag: string;
  minutes: number;
}

export interface QueueAnalytics {
  totalItems: number;
  averageAgeHours: number;
  oldestAgeHours: number;
  newestAgeHours: number;
  totalMinutes: number;
}

/** Per-goal, per-day progress from getTagGoalTrends */
export interface GoalTrendDay {
  date: string; // YYYY-MM-DD
  minutesCompleted: number;
  goalMinutes: number;
  achieved: boolean;
}

export interface GoalTrend {
  goalName: string;
  dailyGoalMinutes: number;
  days: GoalTrendDay[];
  achievementRate: number; // percentage of days goal was met
}

/** From getSchedulingAnalytics */
export interface SchedulingAnalytics {
  scheduledItems: number;
  completedScheduled: number;
  completionRate: number;
  /** Most-used time slots as "HH:MM" strings with counts */
  timeSlots: { slot: string; count: number }[];
}

/** Derived funnel metrics */
export interface FunnelStats {
  feedToQueueRate: number; // percentage
  queueToCompletedRate: number; // percentage
  feedTotal: number;
  queuedTotal: number;
  completedTotal: number;
}

/** Saved time from playback speed */
export interface SavedTime {
  contentMinutes: number;
  actualMinutes: number;
  savedMinutes: number;
}

/** First and last activity timestamps */
export interface ActivityBounds {
  firstCompletion: number; // timestamp ms
  lastCompletion: number; // timestamp ms
}

export interface AnalyticsData {
  overview: OverviewStats;
  dailyActivity: DailyActivity[];
  contentTypes: ContentTypeStat[];
  sources: SourceStat[];
  hourlyActivity: HourlyActivity[];
  dayOfWeek: DayOfWeekActivity[];
  streaks: StreakStat[];
  weeklyComparison: WeeklyComparison;
  tags: TagTime[];
  queue: QueueAnalytics;
  goalTrends: GoalTrend[];
  scheduling: SchedulingAnalytics;
  funnel: FunnelStats;
  savedTime: SavedTime;
  activityBounds: ActivityBounds;
}

function generateDailyActivity(days: number): DailyActivity[] {
  const result: DailyActivity[] = [];
  const now = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const items = Math.floor(Math.random() * 8) + 1;
    result.push({
      date: d.toISOString().slice(0, 10),
      items,
      minutes: items * (Math.floor(Math.random() * 15) + 5),
    });
  }
  return result;
}

function generateHourlyActivity(): HourlyActivity[] {
  const peaks = [8, 9, 12, 13, 19, 20, 21];
  return Array.from({ length: 24 }, (_, hour) => {
    const isPeak = peaks.includes(hour);
    const count = isPeak
      ? Math.floor(Math.random() * 6) + 4
      : Math.floor(Math.random() * 3);
    return { hour, count, minutes: count * 8 };
  });
}

function generateGoalTrends(): GoalTrend[] {
  const goals = [
    { name: "Learn Rust", dailyMin: 30 },
    { name: "Stay Updated", dailyMin: 15 },
    { name: "Design Skills", dailyMin: 20 },
  ];
  const now = new Date();

  return goals.map(({ name, dailyMin }) => {
    const days: GoalTrendDay[] = [];
    let achieved = 0;
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const mins = Math.floor(Math.random() * (dailyMin * 1.8)) + 2;
      const met = mins >= dailyMin;
      if (met) achieved++;
      days.push({
        date: d.toISOString().slice(0, 10),
        minutesCompleted: mins,
        goalMinutes: dailyMin,
        achieved: met,
      });
    }
    return {
      goalName: name,
      dailyGoalMinutes: dailyMin,
      days,
      achievementRate: Math.round((achieved / 7) * 100),
    };
  });
}

const DAY_MS = 86_400_000;

export const MOCK_ANALYTICS: AnalyticsData = {
  overview: {
    today: { items: 5, minutes: 42 },
    thisWeek: { items: 23, minutes: 192 },
    thisMonth: { items: 87, minutes: 714 },
    allTime: { items: 342, minutes: 2856 },
    queueSize: 12,
    feedSize: 48,
  },
  dailyActivity: generateDailyActivity(14),
  contentTypes: [
    { type: "Videos", completedCount: 156, totalCount: 210, completedMinutes: 1240, totalMinutes: 1680 },
    { type: "Podcasts", completedCount: 89, totalCount: 102, completedMinutes: 980, totalMinutes: 1120 },
    { type: "Articles", completedCount: 67, totalCount: 95, completedMinutes: 402, totalMinutes: 570 },
    { type: "Reels", completedCount: 30, totalCount: 44, completedMinutes: 60, totalMinutes: 88 },
  ],
  sources: [
    { name: "Fireship", completedCount: 42, totalCount: 48, completionRate: 87.5, totalMinutes: 210 },
    { name: "Lex Fridman", completedCount: 18, totalCount: 22, completionRate: 81.8, totalMinutes: 540 },
    { name: "The Verge", completedCount: 35, totalCount: 50, completionRate: 70.0, totalMinutes: 175 },
    { name: "Changelog", completedCount: 28, totalCount: 34, completionRate: 82.4, totalMinutes: 336 },
    { name: "Kevin Powell", completedCount: 22, totalCount: 30, completionRate: 73.3, totalMinutes: 132 },
  ],
  hourlyActivity: generateHourlyActivity(),
  dayOfWeek: [
    { day: "Mon", count: 18, minutes: 144 },
    { day: "Tue", count: 24, minutes: 192 },
    { day: "Wed", count: 15, minutes: 120 },
    { day: "Thu", count: 20, minutes: 160 },
    { day: "Fri", count: 12, minutes: 96 },
    { day: "Sat", count: 28, minutes: 224 },
    { day: "Sun", count: 22, minutes: 176 },
  ],
  streaks: [
    { goalName: "Learn Rust", currentStreak: 7, longestStreak: 14, dailyGoalMinutes: 30 },
    { goalName: "Stay Updated", currentStreak: 12, longestStreak: 12, dailyGoalMinutes: 15 },
    { goalName: "Design Skills", currentStreak: 3, longestStreak: 9, dailyGoalMinutes: 20 },
  ],
  weeklyComparison: {
    thisWeek: { items: 23, minutes: 192 },
    lastWeek: { items: 19, minutes: 158 },
    itemsChange: 21,
    minutesChange: 22,
  },
  tags: [
    { tag: "programming", minutes: 680 },
    { tag: "design", minutes: 420 },
    { tag: "productivity", minutes: 310 },
    { tag: "news", minutes: 180 },
    { tag: "science", minutes: 140 },
  ],
  queue: {
    totalItems: 12,
    averageAgeHours: 76,
    oldestAgeHours: 312,
    newestAgeHours: 2,
    totalMinutes: 186,
  },
  goalTrends: generateGoalTrends(),
  scheduling: {
    scheduledItems: 64,
    completedScheduled: 51,
    completionRate: 79.7,
    timeSlots: [
      { slot: "08:00", count: 18 },
      { slot: "12:00", count: 14 },
      { slot: "19:00", count: 22 },
      { slot: "21:00", count: 10 },
    ],
  },
  funnel: {
    feedToQueueRate: 25,
    queueToCompletedRate: 72,
    feedTotal: 480,
    queuedTotal: 120,
    completedTotal: 86,
  },
  savedTime: {
    contentMinutes: 2856,
    actualMinutes: 1428,
    savedMinutes: 1428,
  },
  activityBounds: {
    firstCompletion: Date.now() - 142 * DAY_MS,
    lastCompletion: Date.now() - 2 * 3_600_000,
  },
};
